import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Flag, Folder, ExternalLink, Ban, Check, Trash2, RotateCcw, Plus, ImageOff, AlertTriangle } from "lucide-react";

const MOD_REASONS = [
  { v: "forbidden", l: "Contenu interdit" },
  { v: "scam", l: "Soupçon d'arnaque" },
  { v: "bad-photo", l: "Photo inappropriée" },
  { v: "poor-description", l: "Description insuffisante" },
  { v: "wrong-category", l: "Catégorie incorrecte" },
  { v: "duplicate", l: "Annonce en double" },
  { v: "personal-info", l: "Coordonnées personnelles exposées" },
  { v: "pro-misidentified", l: "Annonce pro mal identifiée" },
  { v: "other", l: "Autre motif" },
];
const STATUS_OPTS = [
  { v: "", l: "Tous (sauf supprimées)" },
  { v: "published", l: "Publiées" },
  { v: "suspended", l: "Suspendues" },
  { v: "refused", l: "Refusées" },
  { v: "expired", l: "Expirées" },
  { v: "sold", l: "Vendues" }, { v: "given", l: "Données" },
];
const STATUS_FR = {
  published: "Publiée", suspended: "Suspendue", refused: "Refusée",
  expired: "Expirée", sold: "Vendue", given: "Donnée", draft: "Brouillon", deleted: "Supprimée",
};

export default function AdminAnnonces() {
  const [tab, setTab] = useState("annonces");
  const [stats, setStats] = useState(null);
  const [list, setList] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [reportedOnly, setReportedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [reports, setReports] = useState([]);
  const [cats, setCats] = useState([]);
  const [modDlg, setModDlg] = useState(null); // {annonce, action}
  const [modReason, setModReason] = useState("other");
  const [catDlg, setCatDlg] = useState(null);

  const loadStats = useCallback(() => api.get("/admin/annonces/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  const loadList = useCallback(async () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (q) p.set("q", q);
    if (reportedOnly) p.set("reported", "true");
    p.set("page", String(page));
    const { data } = await api.get(`/admin/annonces?${p.toString()}`);
    setList(data);
  }, [status, q, reportedOnly, page]);
  const loadReports = useCallback(() => api.get("/admin/annonces/reports?status=new").then((r) => setReports(r.data || [])).catch(() => {}), []);
  const loadCats = useCallback(() => api.get("/admin/annonce-categories").then((r) => setCats(r.data || [])).catch(() => {}), []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === "annonces") loadList(); if (tab === "reports") loadReports(); if (tab === "cats") loadCats(); }, [tab, loadList, loadReports, loadCats]);

  const doModerate = async (annonce, action, reason = "") => {
    try {
      await api.post(`/admin/annonces/${annonce.id}/moderate`, { action, reason });
      toast.success("Décision enregistrée");
      setModDlg(null); loadList(); loadStats(); loadReports();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };
  const openMod = (annonce, action) => { setModReason("other"); setModDlg({ annonce, action }); };
  const resolveReport = async (id) => { await api.post(`/admin/annonces/reports/${id}/resolve`); toast.success("Signalement traité"); loadReports(); loadStats(); };

  const saveCat = async () => {
    try {
      if (catDlg.id) await api.put(`/admin/annonce-categories/${catDlg.id}`, catDlg);
      else await api.post("/admin/annonce-categories", catDlg);
      toast.success("Catégorie enregistrée"); setCatDlg(null); loadCats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const delCat = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ?`)) return;
    const { data } = await api.delete(`/admin/annonce-categories/${c.id}`);
    toast.success(data.deactivated ? "Catégorie désactivée (annonces existantes)" : "Catégorie supprimée");
    loadCats();
  };

  return (
    <div data-testid="admin-annonces-page">
      <h1 className="font-heading text-2xl md:text-3xl font-extrabold flex items-center gap-2 text-slate-900"><Megaphone className="text-[#E05638]" /> Petites annonces</h1>

      {stats && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-annonces-stats">
          <div className="bg-white border border-slate-200 rounded-xl p-3"><div className="text-2xl font-extrabold">{stats.by_status?.published || 0}</div><div className="text-xs text-slate-500">Publiées</div></div>
          <div className="bg-white border border-slate-200 rounded-xl p-3"><div className="text-2xl font-extrabold text-rose-600">{stats.reported_annonces || 0}</div><div className="text-xs text-slate-500">Signalées</div></div>
          <div className="bg-white border border-slate-200 rounded-xl p-3"><div className="text-2xl font-extrabold text-amber-600">{stats.new_reports || 0}</div><div className="text-xs text-slate-500">Signalements à traiter</div></div>
          <div className="bg-white border border-slate-200 rounded-xl p-3"><div className="text-2xl font-extrabold text-slate-400">{(stats.by_status?.sold || 0) + (stats.by_status?.given || 0)}</div><div className="text-xs text-slate-500">Vendues / données</div></div>
        </div>
      )}

      <div className="mt-5 flex gap-2 border-b border-slate-200">
        {[["annonces", "Annonces"], ["reports", "Signalements"], ["cats", "Catégories"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`admin-annonces-tab-${v}`} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === v ? "border-[#E05638] text-[#E05638]" : "border-transparent text-slate-500"}`}>
            {l}{v === "reports" && stats?.new_reports > 0 ? ` (${stats.new_reports})` : ""}
          </button>
        ))}
      </div>

      {/* ONGLET ANNONCES */}
      {tab === "annonces" && (
        <div className="mt-4">
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), loadList())} placeholder="Rechercher titre / commune…" className="sm:max-w-xs" data-testid="admin-annonces-search" />
            <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="sm:max-w-[220px]" data-testid="admin-annonces-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.v || "all"} value={o.v || "all"}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={reportedOnly} onChange={(e) => { setReportedOnly(e.target.checked); setPage(1); }} data-testid="admin-annonces-reported" /> Signalées uniquement</label>
            <Button onClick={() => { setPage(1); loadList(); }} variant="outline" data-testid="admin-annonces-filter">Filtrer</Button>
          </div>

          <div className="text-xs text-slate-500 mb-2">{list.total} annonce(s)</div>
          <div className="space-y-2">
            {list.items.map((a) => (
              <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-3 flex gap-3" data-testid={`admin-annonce-${a.id}`}>
                <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden shrink-0 grid place-items-center">
                  {a.main_image ? <img src={a.main_image} alt="" className="w-full h-full object-cover" /> : <ImageOff size={18} className="text-slate-300" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-slate-900 text-sm line-clamp-1">{a.title}</div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {a.flagged && <span title="Lien/téléphone détecté" className="text-amber-500"><AlertTriangle size={14} /></span>}
                      {a.report_count > 0 && <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-rose-600"><Flag size={11} /> {a.report_count}</span>}
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{STATUS_FR[a.status] || a.status}</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500">{a.price_label} · {a.city} · {a.category_name} · par {a.owner_name}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Link to={`/petites-annonces/annonce/${a.slug}`} target="_blank" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-slate-200 hover:border-slate-400" data-testid={`admin-annonce-view-${a.id}`}><ExternalLink size={12} /> Voir</Link>
                    {a.status !== "suspended" && <button onClick={() => openMod(a, "suspend")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`admin-annonce-suspend-${a.id}`}><Ban size={12} /> Suspendre</button>}
                    {(a.status === "suspended" || a.status === "refused" || a.status === "expired") && <button onClick={() => doModerate(a, "restore")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`admin-annonce-restore-${a.id}`}><RotateCcw size={12} /> Republier</button>}
                    <button onClick={() => openMod(a, "refuse")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`admin-annonce-refuse-${a.id}`}><Check size={12} /> Refuser</button>
                    <button onClick={() => openMod(a, "delete")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`admin-annonce-delete-${a.id}`}><Trash2 size={12} /> Supprimer</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {list.pages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Précédent</Button>
              <span className="text-sm text-slate-600">Page {list.page} / {list.pages}</span>
              <Button variant="outline" size="sm" disabled={page >= list.pages} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
            </div>
          )}
        </div>
      )}

      {/* ONGLET SIGNALEMENTS */}
      {tab === "reports" && (
        <div className="mt-4 space-y-2" data-testid="admin-annonces-reports">
          {reports.length === 0 && <div className="text-sm text-slate-500 bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center">Aucun signalement en attente.</div>}
          {reports.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`admin-report-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{r.annonce?.title || "Annonce supprimée"}</div>
                  <div className="text-xs text-rose-600 font-semibold">{r.reason_label}</div>
                  {r.message && <div className="text-xs text-slate-500 mt-0.5 italic">« {r.message} »</div>}
                  <div className="text-[11px] text-slate-400 mt-0.5">{new Date(r.created_at).toLocaleString("fr-FR")}</div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {r.annonce && <Link to={`/petites-annonces/annonce/${r.annonce.slug}`} target="_blank" className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 hover:border-slate-400"><ExternalLink size={11} /> Voir</Link>}
                  {r.annonce && <button onClick={() => openMod(r.annonce, "suspend")} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50"><Ban size={11} /> Suspendre</button>}
                  <button onClick={() => resolveReport(r.id)} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`admin-report-resolve-${r.id}`}><Check size={11} /> Traité</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ONGLET CATÉGORIES */}
      {tab === "cats" && (
        <div className="mt-4" data-testid="admin-annonces-cats">
          <Button onClick={() => setCatDlg({ name: "", slug: "", order: 99, active: true, restricted: false })} className="mb-3 btn-shine text-white" data-testid="admin-cat-new"><Plus size={15} className="mr-1.5" /> Nouvelle catégorie</Button>
          <div className="space-y-2">
            {cats.map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`admin-cat-${c.id}`}>
                <Folder size={16} className="text-slate-400" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 text-sm">{c.name} {!c.active && <span className="text-xs text-slate-400">(inactive)</span>} {c.restricted && <span className="text-xs text-amber-600">· réglementée</span>}</div>
                  <div className="text-xs text-slate-500">/{c.slug} · ordre {c.order} · {c.count} annonce(s)</div>
                </div>
                <button onClick={() => setCatDlg({ ...c })} className="text-xs px-2 py-1 rounded-lg border border-slate-200 hover:border-slate-400" data-testid={`admin-cat-edit-${c.id}`}>Modifier</button>
                <button onClick={() => delCat(c)} className="text-xs px-2 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`admin-cat-del-${c.id}`}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dialog modération */}
      <Dialog open={!!modDlg} onOpenChange={(o) => !o && setModDlg(null)}>
        <DialogContent data-testid="admin-moderate-dialog">
          <DialogHeader><DialogTitle>{modDlg?.action === "delete" ? "Supprimer l'annonce" : modDlg?.action === "suspend" ? "Suspendre l'annonce" : "Refuser l'annonce"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">« {modDlg?.annonce?.title} » — l'auteur sera informé par e-mail (suspension/refus).</p>
          <Select value={modReason} onValueChange={setModReason}>
            <SelectTrigger data-testid="admin-moderate-reason"><SelectValue /></SelectTrigger>
            <SelectContent>{MOD_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModDlg(null)}>Annuler</Button>
            <Button onClick={() => doModerate(modDlg.annonce, modDlg.action, modReason)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="admin-moderate-confirm">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog catégorie */}
      <Dialog open={!!catDlg} onOpenChange={(o) => !o && setCatDlg(null)}>
        <DialogContent data-testid="admin-cat-dialog">
          <DialogHeader><DialogTitle>{catDlg?.id ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm text-slate-600">Nom</label><Input value={catDlg?.name || ""} onChange={(e) => setCatDlg((c) => ({ ...c, name: e.target.value }))} data-testid="admin-cat-name" /></div>
            <div><label className="text-sm text-slate-600">Ordre d'affichage</label><Input type="number" value={catDlg?.order ?? 99} onChange={(e) => setCatDlg((c) => ({ ...c, order: Number(e.target.value) }))} data-testid="admin-cat-order" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={catDlg?.active ?? true} onChange={(e) => setCatDlg((c) => ({ ...c, active: e.target.checked }))} data-testid="admin-cat-active" /> Active (visible du public)</label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={catDlg?.restricted ?? false} onChange={(e) => setCatDlg((c) => ({ ...c, restricted: e.target.checked }))} data-testid="admin-cat-restricted" /> Réglementée (dépôt bloqué aux particuliers)</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDlg(null)}>Annuler</Button>
            <Button onClick={saveCat} className="btn-shine text-white" data-testid="admin-cat-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
